import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import type { EventRules, SetupEventView, TournamentSetupView } from "@/libs/tournamentSetupApi";

import { PickerField, formatDay } from "../setup/fields";
import { TermHelp } from "../setup/term-help";
import { DenseButton, DenseInput, DenseSelect, FieldLabel, NewBadge, SetupCard, tone } from "../setup/ui";
import { useSetupCommand } from "./use-setup-command";

const LEVEL_NAMES: Record<string, string> = { open: "Open", A: "A", B: "B", C: "C", D: "D" };
const NEXT_LEVEL: Record<string, string | null> = { open: null, A: "B", B: "C", C: "D", D: null };
const SCORING: { value: EventRules["scoring"]; label: string }[] = [
  { value: "21x3", label: "Games to 21, best of 3" },
  { value: "15x3", label: "Games to 15, best of 3" },
  { value: "11x5", label: "Games to 11, best of 5" },
  { value: "21x1", label: "One game to 21" },
];
const GENDER: Record<string, string> = { MS: "Men", WS: "Women", MD: "Men", WD: "Women", XD: "Mixed pairs" };

function Section({ title, children, first }: { title: ReactNode; children: ReactNode; first?: boolean }) {
  return (
    <Box sx={{ p: "10px 14px", borderTop: first ? 0 : `1px solid ${tone.divider}`, display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".3px", color: tone.subtle, display: "flex", alignItems: "center", gap: "5px" }}>{title}</Box>
      {children}
    </Box>
  );
}

function Radio({ name, checked, onChange, children, disabled }: { name: string; checked: boolean; onChange: () => void; children: ReactNode; disabled?: boolean }) {
  return (
    <Box component="label" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13, minHeight: 28, cursor: disabled ? "default" : "pointer", color: disabled ? tone.faint : tone.ink }}>
      <input checked={checked} disabled={disabled} name={name} onChange={onChange} style={{ width: 16, height: 16, accentColor: tone.brand, margin: 0 }} type="radio" />
      {children}
    </Box>
  );
}

/** More options for one event (F8): who can enter, partner, when full,
 * approval, scoring, time, seeding, 3rd place; the format-at-close and
 * discount blocks come with A2. */
export function EventMoreOptions({
  view,
  event,
  onViewChange,
  onSelectEvent,
  onClose,
  onOpenOverview,
}: {
  view: TournamentSetupView;
  event: SetupEventView;
  onViewChange: (next: TournamentSetupView) => void;
  onSelectEvent: (id: string) => void;
  onClose: () => void;
  onOpenOverview: () => void;
}) {
  const { pending, error, run } = useSetupCommand(view, onViewChange);
  const [rules, setRules] = useState<EventRules>(event.rules);
  useEffect(() => setRules(event.rules), [event]);
  const set = <K extends keyof EventRules>(key: K, value: EventRules[K]) => setRules((current) => ({ ...current, [key]: value }));
  const changed = (Object.keys(rules) as (keyof EventRules)[]).filter((key) => JSON.stringify(rules[key]) !== JSON.stringify(event.rules[key]));
  const locks = event.locks as Record<string, { reason: string }>;
  const eligibilityLocked = Boolean(locks.eligibility);
  const singles = event.entry_unit === "players";
  const level = event.cell.level ?? "open";
  const next = NEXT_LEVEL[level];
  const levelValue = (rules.eligible_levels ?? []).length > 1 ? "two" : "one";
  const time = view.time;
  const length = rules.match_minutes ?? (singles ? time.singles_match_minutes : time.doubles_match_minutes);
  const save = async () => {
    const patch = Object.fromEntries(changed.map((key) => [key, rules[key]]));
    if (await run({ command: "update_events", event_ids: [event.event_id], patch: { rules: patch } } as never)) onClose();
  };
  const ageAsOf = rules.age_as_of ?? view.days[0]?.play_date ?? "";

  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", minHeight: 0 }}>
      <SetupCard sx={{ width: 250, flex: "none", overflow: "hidden" }} title={`${view.events.length} events`}>
        {view.events.map((item) => {
          const on = item.event_id === event.event_id;
          return (
            <Box
              component="button"
              key={item.event_id}
              onClick={() => onSelectEvent(item.event_id)}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1,
                minHeight: 34,
                px: "12px",
                border: 0,
                borderTop: `1px solid ${tone.divider}`,
                bgcolor: on ? tone.brandNote : tone.surface,
                boxShadow: on ? "inset 3px 0 0 ${tone.brand}" : "none",
                font: "inherit",
                fontSize: 12.5,
                fontWeight: on ? 700 : 500,
                color: tone.ink,
                textAlign: "left",
                cursor: "pointer",
              }}
              type="button"
            >
              <span>{item.name}</span>
              <Box sx={{ flexGrow: 1 }} />
              <Box component="span" sx={{ fontSize: 11.5, color: tone.subtle, fontWeight: 500 }}>
                {item.day_labels.join(" → ")}
              </Box>
            </Box>
          );
        })}
      </SetupCard>
      <SetupCard
        actions={
          <DenseButton onClick={onClose} size="sm">
            Show less
          </DenseButton>
        }
        extra="More options"
        sx={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
        title={event.name}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
          <Box sx={{ borderRight: `1px solid ${tone.divider}` }}>
            <Section first title="WHO CAN ENTER">
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                <Box>
                  <FieldLabel htmlFor="o-g">Gender</FieldLabel>
                  <DenseSelect disabled id="o-g" value="fixed">
                    <option value="fixed">{GENDER[event.cell.kind]}</option>
                  </DenseSelect>
                </Box>
                <Box>
                  <FieldLabel htmlFor="o-lv">
                    Level <TermHelp label="What is a level?" term="level" />
                  </FieldLabel>
                  <DenseSelect
                    disabled={eligibilityLocked || level === "open"}
                    id="o-lv"
                    onChange={(e) => set("eligible_levels", e.target.value === "two" && next ? [level as never, next as never] : [])}
                    value={levelValue}
                  >
                    <option value="one">{level === "open" ? "Open: anyone" : `${LEVEL_NAMES[level]} only`}</option>
                    {next ? <option value="two">{`${LEVEL_NAMES[level]} or ${LEVEL_NAMES[next]}`}</option> : null}
                  </DenseSelect>
                </Box>
                <Box>
                  <FieldLabel htmlFor="o-a1">Age from</FieldLabel>
                  <DenseInput disabled={eligibilityLocked} id="o-a1" inputMode="numeric" onChange={(e) => set("min_age", e.target.value ? Number(e.target.value) : null)} placeholder="Any" value={rules.min_age ?? ""} />
                </Box>
                <Box>
                  <FieldLabel htmlFor="o-a2">to</FieldLabel>
                  <DenseInput disabled={eligibilityLocked} id="o-a2" inputMode="numeric" onChange={(e) => set("max_age", e.target.value ? Number(e.target.value) : null)} placeholder="Any" value={rules.max_age ?? ""} />
                </Box>
              </Box>
              <Box>
                <FieldLabel>Age as of</FieldLabel>
                <PickerField disabled={eligibilityLocked} display={ageAsOf ? formatDay(ageAsOf, true) : "Dec 31 of the start year"} label="Age as of" onChange={(value) => set("age_as_of", value)} type="date" value={ageAsOf} />
              </Box>
              {eligibilityLocked ? <Box sx={{ fontSize: 12, color: tone.subtle }}>{locks.eligibility.reason}</Box> : null}
            </Section>
            {!singles ? (
              <Section title="PARTNER">
                {/* D37: every doubles player enters alone, then pairs up; the
                    pairing deadline setting arrives with A2. */}
                <Box sx={{ fontSize: 12.5, color: tone.muted, lineHeight: 1.5 }}>Each player enters on their own, then pairs up from My entries.</Box>
              </Section>
            ) : null}
            <Section title="ENTRY TYPE">
              <Box sx={{ fontSize: 13, color: tone.ink2 }}>Players and pairs. Team entries come later.</Box>
            </Section>
            <Section title="WHEN FULL · MINIMUM · APPROVAL">
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                <Box>
                  <FieldLabel htmlFor="o-full">When full</FieldLabel>
                  <DenseSelect id="o-full" onChange={(e) => set("when_full", e.target.value as EventRules["when_full"])} value={rules.when_full}>
                    <option value="waitlist">Waitlist</option>
                    <option value="close">Close entries</option>
                  </DenseSelect>
                </Box>
                <Box>
                  <FieldLabel htmlFor="o-min">Run with at least</FieldLabel>
                  <DenseSelect id="o-min" onChange={(e) => set("minimum_entries", Number(e.target.value))} value={rules.minimum_entries}>
                    {[2, 3, 4, 6, 8, 12, 16].map((n) => (
                      <option key={n} value={n}>
                        {n} {event.entry_unit}
                      </option>
                    ))}
                  </DenseSelect>
                </Box>
              </Box>
              <Radio checked={rules.auto_accept} name="ap" onChange={() => set("auto_accept", true)}>
                Accept entries automatically
              </Radio>
              <Radio checked={!rules.auto_accept} name="ap" onChange={() => set("auto_accept", false)}>
                I approve each entry
              </Radio>
            </Section>
          </Box>
          <Box sx={{ borderRight: `1px solid ${tone.divider}` }}>
            <Section first title="SCORING">
              <DenseSelect aria-label="Scoring" onChange={(e) => set("scoring", e.target.value as EventRules["scoring"])} value={rules.scoring}>
                {SCORING.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </DenseSelect>
            </Section>
            <Section title="TIME FOR THIS EVENT">
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1 }}>
                <Box>
                  <FieldLabel htmlFor="o-len">Match length</FieldLabel>
                  <DenseInput id="o-len" inputMode="numeric" mono onChange={(e) => set("match_minutes", e.target.value ? Number(e.target.value) : null)} value={length} />
                </Box>
                <Box>
                  <FieldLabel htmlFor="o-wu">Warm-up</FieldLabel>
                  <DenseSelect disabled id="o-wu" value="t">
                    <option value="t">{time.warmup_minutes} min (tournament)</option>
                  </DenseSelect>
                </Box>
                <Box>
                  <FieldLabel htmlFor="o-rest">
                    Minimum rest <TermHelp label="What is minimum rest?" term="minimum_rest" />
                  </FieldLabel>
                  <DenseSelect id="o-rest" onChange={(e) => set("rest_minutes", e.target.value ? Number(e.target.value) : null)} value={rules.rest_minutes ?? ""}>
                    <option value="">{time.minimum_rest_minutes} min (default)</option>
                    {[10, 15, 20, 30, 45, 60].map((n) => (
                      <option key={n} value={n}>
                        {n} min
                      </option>
                    ))}
                  </DenseSelect>
                </Box>
              </Box>
              <Box sx={{ fontSize: 12, color: tone.subtle }}>
                Court time per match: {time.warmup_minutes} + {length} + {time.changeover_minutes} min changeover = {time.warmup_minutes + length + time.changeover_minutes} min.
              </Box>
            </Section>
          </Box>
          <Box>
            <Section first title="SEEDING">
              <Radio checked={rules.seeding === "ratings"} name="sd" onChange={() => set("seeding", "ratings")}>
                Seeds from BW ratings <NewBadge />
              </Radio>
              <Radio checked={rules.seeding === "manual"} name="sd" onChange={() => set("seeding", "manual")}>
                I set seeds by hand
              </Radio>
              <Radio checked={rules.seeding === "none"} name="sd" onChange={() => set("seeding", "none")}>
                No seeds
              </Radio>
              <Box>
                <FieldLabel htmlFor="o-ns">Number of seeds</FieldLabel>
                <DenseSelect disabled={rules.seeding === "none"} id="o-ns" onChange={(e) => set("seed_count", Number(e.target.value) as EventRules["seed_count"])} value={rules.seed_count}>
                  {[2, 4, 8].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </DenseSelect>
              </Box>
            </Section>
            <Section title="3RD-PLACE MATCH">
              <Box component="label" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13, color: event.format === "knockout" ? tone.ink : tone.faint }}>
                <input
                  checked={rules.third_place_match}
                  disabled={event.format !== "knockout"}
                  onChange={(e) => set("third_place_match", e.target.checked)}
                  style={{ width: 15, height: 15, accentColor: tone.brand, margin: 0 }}
                  type="checkbox"
                />
                Play a match for 3rd place
              </Box>
              <Box sx={{ fontSize: 12, color: tone.subtle }}>
                {event.format === "knockout" ? "Adds 1 match. It waits for both semifinal losers." : "Only with knockout. Consolation and groups already place players."}
              </Box>
            </Section>
            <Section title="TOURNAMENT-WIDE">
              <Box sx={{ fontSize: 12.5, color: tone.ink2 }}>
                Players can enter{" "}
                <b>{view.limits.max_events_per_player === null || view.limits.max_events_per_player === undefined ? "any number of events" : `up to ${view.limits.max_events_per_player} events`}</b>.
              </Box>
              <Box component="label" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13 }}>
                <input checked={view.limits.one_level_per_discipline ?? true} disabled readOnly style={{ width: 15, height: 15, accentColor: tone.brand, margin: 0 }} type="checkbox" />
                Players can enter one level per discipline
              </Box>
              <Box sx={{ fontSize: 12, color: tone.subtle }}>
                On by default, so nobody plays Men’s singles Open and B. Keeps the rest check at 2 events a day.{" "}
                <DenseButton onClick={onOpenOverview} size="sm" sx={{ height: 24 }}>
                  Change on Overview
                </DenseButton>
              </Box>
            </Section>
            <Section title="FEE">
              <Box sx={{ fontSize: 12.5, color: tone.ink2 }}>{event.fee_label} per entry</Box>
            </Section>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", p: "10px 14px", borderTop: `1px solid ${tone.divider}`, bgcolor: tone.page, mt: "auto" }}>
          <Box component="span" sx={{ fontSize: 12, color: error ? tone.badText : tone.muted }} role={error ? "alert" : undefined}>
            {error ?? (
              <>
                <b>Summary line:</b> {event.rules_summary}
              </>
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <DenseButton onClick={onClose}>Cancel</DenseButton>
          <DenseButton disabled={pending || !changed.length} onClick={save} variant="primary">
            Save
          </DenseButton>
        </Box>
      </SetupCard>
    </Box>
  );
}
